import { openLocalDatabase, RESULTS_OWNER_INDEX, SCOPED_RESULTS_STORE } from './database';

const STORE = SCOPED_RESULTS_STORE;
const OWNER_INDEX = RESULTS_OWNER_INDEX;

export interface StorageScope { studioId: string; telegramUserId: number }

export interface StoredResult {
  sessionId: string;
  clean: Blob;
  preview: Blob;
  createdAt: number;
  designId?: string;
  designName?: string;
  designCategory?: string;
  custom?: boolean;
  cloudSaved?: boolean;
  bookingStatus?: string;
}

interface ScopedStoredResult extends StoredResult {
  cacheKey: string;
  ownerKey: string;
}

function withoutScope(value: ScopedStoredResult): StoredResult {
  return {
    sessionId: value.sessionId, clean: value.clean, preview: value.preview, createdAt: value.createdAt,
    ...(value.designId !== undefined ? { designId: value.designId } : {}),
    ...(value.designName !== undefined ? { designName: value.designName } : {}),
    ...(value.designCategory !== undefined ? { designCategory: value.designCategory } : {}),
    ...(value.custom !== undefined ? { custom: value.custom } : {}),
    ...(value.cloudSaved !== undefined ? { cloudSaved: value.cloudSaved } : {}),
    ...(value.bookingStatus !== undefined ? { bookingStatus: value.bookingStatus } : {}),
  };
}

function cleanSegment(value: string | number): string { return encodeURIComponent(String(value)); }
export function storageOwnerKey(scope: StorageScope): string {
  return `${cleanSegment(scope.studioId)}:${cleanSegment(scope.telegramUserId)}`;
}
export function storageCacheKey(scope: StorageScope, sessionId: string): string {
  return `${storageOwnerKey(scope)}:${cleanSegment(sessionId)}`;
}
export function belongsToStorageScope(value: { ownerKey?: string }, scope: StorageScope): boolean {
  return value.ownerKey === storageOwnerKey(scope);
}

export async function storeResult(scope: StorageScope, result: StoredResult): Promise<void> {
  const database = await openLocalDatabase();
  const scoped: ScopedStoredResult = { ...result, ownerKey: storageOwnerKey(scope), cacheKey: storageCacheKey(scope, result.sessionId) };
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE, 'readwrite');
    transaction.objectStore(STORE).put(scoped);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('The local result could not be saved.'));
  });
  database.close();
}

export async function getStoredResult(scope: StorageScope, sessionId: string): Promise<StoredResult | null> {
  const database = await openLocalDatabase();
  const result = await new Promise<ScopedStoredResult | null>((resolve, reject) => {
    const request = database.transaction(STORE, 'readonly').objectStore(STORE).get(storageCacheKey(scope, sessionId));
    request.onsuccess = () => resolve((request.result as ScopedStoredResult | undefined) || null);
    request.onerror = () => reject(request.error || new Error('The local result could not be loaded.'));
  });
  database.close();
  if (!result || !belongsToStorageScope(result, scope)) return null;
  return withoutScope(result);
}

export async function listStoredResults(scope: StorageScope): Promise<StoredResult[]> {
  const database = await openLocalDatabase();
  const results = await new Promise<ScopedStoredResult[]>((resolve, reject) => {
    const request = database.transaction(STORE, 'readonly').objectStore(STORE).index(OWNER_INDEX).getAll(storageOwnerKey(scope));
    request.onsuccess = () => resolve((request.result as ScopedStoredResult[]).filter((item) => belongsToStorageScope(item, scope)));
    request.onerror = () => reject(request.error || new Error('Local Try-Ons could not be loaded.'));
  });
  database.close();
  return results.map(withoutScope).sort((left, right) => right.createdAt - left.createdAt);
}

export async function deleteStoredResult(scope: StorageScope, sessionId: string): Promise<void> {
  const database = await openLocalDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE, 'readwrite');
    transaction.objectStore(STORE).delete(storageCacheKey(scope, sessionId));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('The local Try-On could not be deleted.'));
  });
  database.close();
}

export async function updateStoredResult(scope: StorageScope, sessionId: string, changes: Partial<Omit<StoredResult, 'sessionId' | 'clean' | 'preview' | 'createdAt'>>): Promise<void> {
  const current = await getStoredResult(scope, sessionId);
  if (!current) return;
  await storeResult(scope, { ...current, ...changes });
}

export async function clearStoredResults(scope: StorageScope): Promise<void> {
  const database = await openLocalDatabase();
  const keys = await new Promise<IDBValidKey[]>((resolve, reject) => {
    const request = database.transaction(STORE, 'readonly').objectStore(STORE).index(OWNER_INDEX).getAllKeys(storageOwnerKey(scope));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE, 'readwrite');
    const store = transaction.objectStore(STORE);
    keys.forEach((key) => store.delete(key));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}
